import { checkTerm, normalizeTerm } from '@dispositio/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { de } from '../../i18n/de';

interface Props {
  /** Name des gewählten Elements (Überschrift) */
  name: string;
  /** Wörter, mit denen es schon im Text vorkommt: der Name zuerst, dann seine Vorkommen */
  terms: string[];
  /** Lage des „@“ im Fenster */
  anchor: { left: number; top: number; bottom: number };
  /** Wort an die Stelle des „@“ setzen (verknüpft); ein neues lernt das Element als Vorkommen */
  onPick: (word: string) => void;
  /** `back`: mit Esc bzw. ✕ geschlossen – der Cursor kehrt an die Stelle im Text zurück */
  onClose: (back: boolean) => void;
}

const WIDTH = 360;
const HEIGHT = 380;

/**
 * Zweiter Schritt nach einem allein stehenden „@“: Mit welchem Wort steht das gewählte Element an dieser Stelle im Text? Zur Wahl
 * stehen sein Name und seine Vorkommen („Clärchen“, „die Kapitänin“); das Feld oben filtert sie, und was noch nicht vorkommt, wird mit
 * Enter als neues Wort eingesetzt.
 */
export function TermPicker({ name, terms, anchor, onPick, onClose }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const typed = normalizeTerm(query);
  const shown = useMemo(() => terms.filter((t) => t.toLowerCase().includes(typed.toLowerCase())), [terms, typed]);
  // ein getipptes Wort, das es noch nicht gibt, steht als letzte Wahl darunter
  const fresh = typed !== '' && !terms.some((t) => t.toLowerCase() === typed.toLowerCase()) ? typed : null;
  const error = fresh ? checkTerm(fresh) : null;
  const options = [...shown, ...(fresh && !error ? [fresh] : [])];

  useEffect(() => {
    setActive(0);
  }, [typed]);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [active]);
  useEffect(() => {
    const onDown = (e: MouseEvent): void => {
      if (host.current && !host.current.contains(e.target as Node)) onClose(false);
    };
    window.addEventListener('mousedown', onDown, true);
    return () => window.removeEventListener('mousedown', onDown, true);
  }, [onClose]);

  const left = Math.max(8, Math.min(anchor.left, window.innerWidth - WIDTH - 8));
  const below = anchor.bottom + 8;
  const top = below + HEIGHT > window.innerHeight - 8 ? Math.max(8, anchor.top - HEIGHT - 8) : below;
  const heading = de.termPicker.title(name);

  return (
    <div
      ref={host}
      className="ref-browser term-picker"
      style={{ left, top, width: WIDTH, maxHeight: HEIGHT }}
      role="dialog"
      aria-label={heading}
      data-testid="term-picker"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          onClose(true);
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          if (options.length > 0) setActive((a) => (a + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length);
        } else if (e.key === 'Enter') {
          e.preventDefault();
          const word = options[Math.min(active, options.length - 1)];
          if (word) onPick(word);
        }
      }}
    >
      <div className="rb-head">
        <strong className="grow rb-heading">{heading}</strong>
        <button type="button" className="btn ghost icon" onClick={() => onClose(true)} aria-label={de.common.close} data-testid="tp-close">
          ✕
        </button>
      </div>
      <input
        type="search"
        autoFocus
        className="rb-search"
        placeholder={de.termPicker.search}
        aria-label={de.termPicker.search}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        data-testid="tp-search"
      />
      <div className="rb-list" role="listbox" aria-label={heading}>
        {options.map((word, i) => {
          const isNew = word === fresh;
          return (
            <button
              key={`${isNew ? 'new' : 'term'}:${word}`}
              ref={i === active ? activeRef : undefined}
              type="button"
              role="option"
              aria-selected={i === active}
              className={`rb-item tp-item${i === active ? ' active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => onPick(word)}
              data-testid={isNew ? 'tp-new' : 'tp-term'}
            >
              <span className="rb-title grow">{isNew ? de.termPicker.useNew(word) : word}</span>
              {!isNew && i === 0 && word === terms[0] && typed === '' && <span className="badge">{de.termPicker.name}</span>}
            </button>
          );
        })}
        {options.length === 0 && <div className="muted small rb-none">{error ?? de.termPicker.none}</div>}
      </div>
      {error && options.length > 0 && <div className="small danger-text">{error}</div>}
      <div className="muted small rb-hint">{de.termPicker.hint}</div>
    </div>
  );
}
